import { Button, cn, EmptyState, Skeleton, toast } from '@paguz/ui';
import { AlertTriangleIcon, CheckIcon, CopyIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { describeError, ApiError } from '../api/client.ts';

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-sm text-muted-foreground">{eyebrow}</div>}
        <h1 className="font-display text-3xl font-semibold text-primary">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Tarjeta de sección de la consola (más compacta que `Card` de móvil). */
export function Panel({
  title,
  description,
  actions,
  children,
  className,
  id,
  bodyClassName,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={cn('rounded-2xl border border-border bg-card shadow-xs', className)}
    >
      {(title ?? actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            {title && (
              <h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>
            )}
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn('p-5', bodyClassName)}>{children}</div>
    </section>
  );
}

/** Lista de pares etiqueta/valor. */
export function DataList({
  items,
  columns = 2,
  className,
}: {
  items: readonly { label: ReactNode; value: ReactNode; hint?: ReactNode }[];
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const grid = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
    4: 'grid-cols-2 xl:grid-cols-4',
  }[columns];
  return (
    <dl className={cn('grid gap-x-6 gap-y-4', grid, className)}>
      {items.map((item, index) => (
        <div key={index} className="min-w-0">
          <dt className="text-xs font-semibold text-muted-foreground">{item.label}</dt>
          <dd className="mt-1 min-w-0 text-sm text-foreground">{item.value}</dd>
          {item.hint && <dd className="text-xs text-muted-foreground">{item.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** Valor técnico copiable (IDs, `traceId`, `endToEndId`). */
export function CopyValue({
  value,
  label,
  display,
  className,
}: {
  value: string | null | undefined;
  label?: string;
  display?: ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1', className)}>
      <code className="truncate rounded-md bg-muted px-1.5 py-0.5 font-mono text-[0.8125rem] text-foreground">
        {display ?? value}
      </code>
      <button
        type="button"
        aria-label={`Copiar ${label ?? value}`}
        title="Copiar"
        className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
        onClick={() => {
          void navigator.clipboard
            .writeText(value)
            .then(() => {
              setCopied(true);
              toast.success(`${label ?? 'Valor'} copiado`);
              setTimeout(() => {
                setCopied(false);
              }, 1500);
            })
            .catch(() => toast.error('No se pudo copiar'));
        }}
      >
        {copied ? <CheckIcon className="size-4 text-success" /> : <CopyIcon className="size-4" />}
      </button>
    </span>
  );
}

export function LoadingRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const traceId = error instanceof ApiError ? error.traceId : null;
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <EmptyState
      icon={<AlertTriangleIcon />}
      title={notFound ? 'No encontramos lo que buscas' : 'No pudimos cargar la información'}
      description={
        <>
          {describeError(error)}
          {traceId && (
            <span className="mt-2 block text-xs">
              traceId: <code className="font-mono">{traceId}</code>
            </span>
          )}
        </>
      }
      action={
        onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Reintentar
          </Button>
        )
      }
    />
  );
}

/** Bloque JSON saneado (request/response de llamadas externas, antes/después de auditoría). */
export function JsonBlock({ value, className }: { value: unknown; className?: string }) {
  return (
    <pre
      className={cn(
        'max-h-72 overflow-auto rounded-lg bg-ink p-3 font-mono text-xs leading-relaxed text-slate-100',
        className,
      )}
    >
      {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
    </pre>
  );
}
